import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import cn from 'classnames'
import type {CustomerSpotlightSliceDefault} from 'prismicio-types'
import {Container} from 'src/common/Container'
import {StarRating} from 'src/common/StarRating'

export const DefaultVariation = ({slice}: {slice: CustomerSpotlightSliceDefault}): JSX.Element => {
  const {
    primary: {
      image_right_side: isImageRight,
      background_color,
      image,
      ratings,
      card_background_color,
      testimony,
      author,
      customer_icon,
      enable_reskin,
    },
    variation,
  } = slice

  const isDefault = variation === 'default'

  return (
    <Container data-slice-variation={slice.variation} backgroundColor={background_color}>
      <div
        className={cn('grid w-full max-w-[1200px] grid-cols-1 gap-4 px-5 xl:px-0', {
          'md:grid-cols-2 md:items-center': isDefault,
          'lg:grid-cols-[auto_38%] xl:grid-cols-[auto_494px]': isDefault && isImageRight,
          'lg:grid-cols-[38%_auto] xl:grid-cols-[494px_auto]': isDefault && !isImageRight,
        })}
        data-aos="fade-up"
      >
        <div
          className={cn('relative', {
            'md:order-2': isImageRight,
          })}
        >
          <PrismicNextImage field={image} className="rounded-lg" />
          {isFilled.group(ratings) && (
            <div
              className={cn(
                'absolute bottom-6 z-10 flex w-full flex-col items-center justify-center gap-2 lg:flex-row',
                {
                  'lg:justify-start px-6': enable_reskin,
                },
              )}
            >
              {ratings.map((platform, index) => (
                <div
                  className={cn('flex h-10 w-min items-center gap-1.5 bg-moss-white px-4', {
                    'rounded-lg': !enable_reskin,
                    'rounded-[4px]': enable_reskin,
                  })}
                  key={platform.platform || '' + index}
                >
                  <span
                    className={cn({
                      'text-body-s': !enable_reskin,
                      'font-medium text-tg-body-m': enable_reskin,
                    })}
                  >
                    {platform.platform}
                  </span>
                  <StarRating rating={platform.rating!} />
                  <span
                    className={cn('text-body-s', {
                      'font-medium text-tg-body-m': enable_reskin,
                    })}
                  >
                    {platform.rating}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div
          className={cn(
            'flex w-full flex-col gap-8 rounded-lg px-6 py-8 md:h-full md:justify-between md:gap-4 md:px-8 lg:gap-8 lg:p-12',
            {
              'bg-beige-200': background_color === 'Moss White' && !isFilled.select(card_background_color),
              'bg-moss-white':
                (background_color === 'Beige/100' || !background_color) && !isFilled.select(card_background_color),
              'bg-accent-neon': card_background_color === 'Neon Green',
              'bg-moss-black': card_background_color === 'Moss Black',
              'bg-green-dark': card_background_color === 'Dark Green',
            },
          )}
        >
          <div
            className={cn('flex flex-col font-medium lg:max-w-[540px]', {
              'gap-6': enable_reskin,
              'gap-3': !enable_reskin,
            })}
          >
            <p
              className={cn('leading-7 text-moss-black', {
                'text-moss-white text-[24px] lg:text-[32px] lg:leading-[38px]': enable_reskin,
                'text-20 lg:text-24 lg:leading-8': !enable_reskin,
              })}
            >
              {testimony}
            </p>
            <p
              className={cn({
                'text-grey-550 text-[15px] font-normal leading-6 tracking-[.15px]': !enable_reskin,
                'text-moss-white/[70%] text-button-s uppercase': enable_reskin,
              })}
            >
              {author}
            </p>
          </div>
          {isFilled.image(customer_icon) && (
            <img src={customer_icon.url} alt={customer_icon.alt || ''} className="self-start" />
          )}
        </div>
      </div>
    </Container>
  )
}
